/**
 * Phase 14 — what the editor knows about the master the draft follows:
 * the master's effective config (fetched once through the detail query),
 * which sections are the container's own, and the two moves (flip a
 * section, open the master). Editing an inherited section overrides it for
 * this container on the first change — no switch to flip first — and
 * editing it back to the master's values follows the master again. One
 * context so every tab reads it without prop threading; a standalone
 * container or a master reads "not following".
 * For Media Devoted | Created By Ciro Autiero
 */
import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import type { DropGenEffectiveConfig, DropGenMasterSummary, DropGenSection } from '@trafficpulse/shared';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import type { EditorTabKey } from '../tabs/tab-chrome';
import { useContainerDetail } from '../../../useDropGenerator';
import { editedSections, matchingSections } from '../state/master/own-sections';
import type { ContainerEditor } from '../state/useContainerEditor';

export interface SectionSources {
  following: boolean;
  master: DropGenEffectiveConfig | null;
  masterRef: DropGenMasterSummary | null;
  masters: DropGenMasterSummary[];
  /** The campaign group open on the page (null = all groups). */
  groupKey: string | null;
  isOwn: (section: DropGenSection) => boolean;
  setOwn: (section: DropGenSection, own: boolean) => void;
  /** Opens the master config's pop-up — on one of its tabs when asked (20/10/2026: "Edit the cap" → Auto-send). */
  openMaster: (id: string | 'new', etab?: EditorTabKey) => void;
}

const STANDALONE: SectionSources = {
  following: false,
  master: null,
  masterRef: null,
  masters: [],
  groupKey: null,
  isOwn: () => true,
  setOwn: () => undefined,
  openMaster: () => undefined,
};

const Ctx = createContext<SectionSources>(STANDALONE);

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  groupKey: string | null;
  onOpenMaster: (id: string | 'new', etab?: EditorTabKey) => void;
  children: ReactNode;
}

export const SectionSourcesProvider: FC<Props> = ({ editor, plan, groupKey, onOpenMaster, children }) => {
  const { draft, setSectionSource } = editor;
  const following = draft.kind !== 'MASTER' && draft.masterId !== null;
  const masterDetail = useContainerDetail(following ? draft.masterId : null);
  const master = following ? (masterDetail.data?.effective ?? null) : null;
  // The first edit inside an inherited section makes it this container's own;
  // editing it back to the master's values makes it follow again.
  const edited = useRef(new Set<DropGenSection>());
  useEffect(() => {
    if (!master) return;
    for (const section of editedSections(draft, master)) {
      edited.current.add(section);
      setSectionSource(section, true, master);
    }
    for (const section of matchingSections(draft, master, edited.current)) {
      edited.current.delete(section);
      setSectionSource(section, false, master);
    }
  }, [draft, master, setSectionSource]);
  const value = useMemo<SectionSources>(() => {
    return {
      following,
      master,
      masterRef: (plan.masters ?? []).find((m) => m.id === draft.masterId) ?? null,
      masters: plan.masters ?? [],
      groupKey,
      isOwn: (section) => !following || draft.ownSections[section] === true,
      setOwn: (section, own) => setSectionSource(section, own, master),
      openMaster: onOpenMaster,
    };
  }, [following, master, plan.masters, groupKey, draft.masterId, draft.ownSections, setSectionSource, onOpenMaster]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useSectionSources = () => useContext(Ctx);
